import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { improveApi } from '../../api/improve'
import { RefusedError } from '../../api/client'
import { EntityPicker, type Picked } from '../../components/Correct/EntityPicker'
import { useSpoke } from '../../hooks/useSpoke'
import { ruleSentence, SCOPE_BLURB, SCOPE_WORDS } from '../../lib/improve'
import { MIN_REASON } from '../../types/corrections'
import type { RuleKind, RuleScope } from '../../types/improve'

// "Adding new improvements": state the rule yourself rather than waiting for the loop to infer it. What this
// screen will NOT do is activate it. Everything a person states here lands on the ledger as a correction
// carrying the proposal, and tonight's run verifies it against the regression bank exactly like anything the
// loop proposed itself. A rule nobody checked is a rule nobody can trust.

type FieldKind = 'text' | 'number' | 'select' | 'company' | 'person'

interface Field {
  /** where it lands in the definition: 'kind', or 'match.domain' for a nested one */
  path: string
  label: string
  kind: FieldKind
  options?: { value: string; label: string }[]
  placeholder?: string
  hint?: string
  required?: boolean
}

interface Form {
  label: string
  blurb: string
  /** an entity rule names one thing; a pattern rule generalises and so is anti-gaming checked */
  scope: RuleScope
  /** a field whose presence flips the scope to entity (company-kind by company id, for instance) */
  entityWhen?: string
  fields: Field[]
  /** at least one of these must be filled for the rule to say anything */
  oneOf?: string[]
}

const COMPANY_KINDS = ['customer', 'prospect', 'vendor', 'partner', 'competitor', 'other'].map((v) => ({ value: v, label: v }))
const LIFECYCLE_KINDS = ['lead', 'opportunity', 'proposal', 'negotiation', 'delivery'].map((v) => ({ value: v, label: v }))

const FORMS: Record<RuleKind, Form> = {
  'company-kind': {
    label: 'What kind of company something is',
    blurb: 'Read before the model on every deal scan, and by the contact watch and the account views.',
    scope: 'pattern', entityWhen: 'match.companyId', oneOf: ['match.domain', 'match.companyId', 'match.nameContains'],
    fields: [
      { path: 'match.domain', label: 'Mail domain', kind: 'text', placeholder: 'frostbank.com', hint: 'Everyone writing from this domain.' },
      { path: 'match.nameContains', label: 'Or a name that contains', kind: 'text', placeholder: 'Bank' },
      { path: 'match.companyId', label: 'Or one company on file', kind: 'company' },
      { path: 'kind', label: 'is a', kind: 'select', options: COMPANY_KINDS, required: true },
    ],
  },
  'company-parent': {
    label: 'One company rolls up to another',
    blurb: 'The account list hides the child under the parent; the account plan, the calendar and the weekly roll up to it.',
    scope: 'entity',
    fields: [
      { path: 'childCompanyId', label: 'Subsidiary', kind: 'company', required: true },
      { path: 'parentCompanyId', label: 'Belongs to', kind: 'company', required: true },
    ],
  },
  'person-company': {
    label: 'Who someone actually works for',
    blurb: 'The deal scan will never re-parent this person against the rule, and the contact watch reads it too.',
    scope: 'entity',
    fields: [
      { path: 'personId', label: 'Person', kind: 'person', required: true },
      { path: 'companyId', label: 'Is at', kind: 'company', required: true },
      { path: 'role', label: 'As', kind: 'text', placeholder: 'CFO' },
    ],
  },
  'firm-domain': {
    label: 'A domain that is the firm’s own',
    blurb: 'Mail from here is a colleague, never a counterparty. Unioned with the deployment’s own list at read time.',
    scope: 'pattern',
    fields: [{ path: 'domain', label: 'Domain', kind: 'text', placeholder: 'pppllc.com', required: true }],
  },
  'public-domain': {
    label: 'A public mail host',
    blurb: 'An address here never names a company, so nothing is inferred from the domain.',
    scope: 'pattern',
    fields: [{ path: 'domain', label: 'Domain', kind: 'text', placeholder: 'outlook.com', required: true }],
  },
  competitor: {
    label: 'A rival',
    blurb: 'Seeing them in a room at one of your accounts becomes a threat, never an opportunity.',
    scope: 'pattern', oneOf: ['name', 'domain'],
    fields: [
      { path: 'name', label: 'Name', kind: 'text', placeholder: 'Northstar Advisory' },
      { path: 'domain', label: 'Mail domain', kind: 'text', placeholder: 'northstar.com' },
    ],
  },
  lifecycle: {
    label: 'How long a pursuit stays alive',
    blurb: 'Overrides the lifespan table for one kind: when it starts reading as aging, and when it becomes a missed opportunity.',
    scope: 'pattern',
    fields: [
      { path: 'kind', label: 'For a', kind: 'select', options: LIFECYCLE_KINDS, required: true },
      { path: 'agingAfterDays', label: 'Ages after (days quiet)', kind: 'number', required: true },
      { path: 'missedAfterDays', label: 'Missed after (days quiet)', kind: 'number', required: true },
    ],
  },
  'next-step-grace': {
    label: 'How long a next step stays actionable',
    blurb: 'The grace period past a next step’s date before Scout stops counting it as owed.',
    scope: 'pattern',
    fields: [{ path: 'days', label: 'Grace, in days', kind: 'number', required: true }],
  },
  'classification-cue': {
    label: 'A cue for the model',
    blurb: 'Rendered as a typed cue line in that surface’s prompt. It cannot exceed 240 characters, and a pattern cue may not name the company that produced it.',
    scope: 'pattern',
    fields: [
      { path: 'surface', label: 'Read by', kind: 'select', required: true, options: [{ value: 'deal-scan', label: 'the deal scan' }, { value: 'threat-watch', label: 'the threat watch' }] },
      { path: 'when', label: 'When', kind: 'text', placeholder: 'the thread is about an audit engagement letter', required: true },
      { path: 'verdict.field', label: 'Then set', kind: 'select', required: true, options: [{ value: 'stage', label: 'the stage' }, { value: 'companyKind', label: 'the company kind' }, { value: 'role', label: 'the role' }, { value: 'threat', label: 'threat' }] },
      { path: 'verdict.value', label: 'to', kind: 'text', placeholder: 'vendor', required: true },
    ],
  },
}

const KINDS = Object.keys(FORMS) as RuleKind[]

export function AddImprovement({ onRecorded }: { onRecorded: (correctionId: string) => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [kind, setKind] = useState<RuleKind | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [picked, setPicked] = useState<Record<string, Picked | null>>({})
  const [because, setBecause] = useState('')

  const form = kind ? FORMS[kind] : null
  const set = (path: string, value: string) => setValues((v) => ({ ...v, [path]: value }))
  const reset = () => { setValues({}); setPicked({}); setBecause('') }

  const definition = useMemo(() => build(form, values), [form, values])
  const scope: RuleScope = form ? (form.entityWhen && values[form.entityWhen] ? 'entity' : form.scope) : 'pattern'

  const missing = form
    ? form.fields.filter((f) => f.required && !values[f.path]).map((f) => f.label)
    : []
  const oneOfMissing = !!form?.oneOf && !form.oneOf.some((p) => values[p])
  const enough = !!form && missing.length === 0 && !oneOfMissing && because.trim().length >= MIN_REASON

  const save = useMutation({
    mutationFn: () => improveApi.addRule(spoke, { kind: kind!, scope, definition, because: because.trim() }),
    onSuccess: (correction) => {
      reset(); setKind(null)
      void qc.invalidateQueries({ queryKey: ['improve'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      onRecorded(correction.correctionId)
    },
  })

  const preview = kind ? ruleSentence(kind, definition) : null

  return (
    <div className="card add-card">
      <div className="card-head">
        <div>
          <h2>Add an improvement</h2>
          <p className="muted">
            State the rule yourself instead of waiting for a correction to imply it. It goes on the ledger as a
            proposal and is verified tonight — replayed against every correction already on file — before it changes
            anything. Nothing here takes effect the moment you press save.
          </p>
        </div>
      </div>

      <div className="add-kinds">
        {KINDS.map((k) => (
          <button key={k} className={`chip-filter ${kind === k ? 'on' : ''}`} onClick={() => { setKind(kind === k ? null : k); reset() }}>
            {FORMS[k].label}
          </button>
        ))}
      </div>

      {form && kind && (
        <>
          <p className="add-blurb muted small">{form.blurb}</p>

          <div className="add-fields">
            {form.fields.map((f) => (
              <label key={f.path} className="add-field">
                <span>{f.label}</span>
                {f.kind === 'select' && (
                  <select value={values[f.path] ?? ''} onChange={(ev) => set(f.path, ev.target.value)}>
                    <option value="">choose…</option>
                    {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                {(f.kind === 'text' || f.kind === 'number') && (
                  <input
                    type={f.kind === 'number' ? 'number' : 'text'}
                    value={values[f.path] ?? ''}
                    placeholder={f.placeholder}
                    onChange={(ev) => set(f.path, ev.target.value)}
                  />
                )}
                {(f.kind === 'company' || f.kind === 'person') && (
                  <EntityPicker
                    targetKind={f.kind}
                    picked={picked[f.path] ?? null}
                    onPick={(p) => { setPicked((v) => ({ ...v, [f.path]: p })); set(f.path, p?.id ?? '') }}
                  />
                )}
                {f.hint && <span className="muted small">{f.hint}</span>}
              </label>
            ))}
          </div>

          <p className="add-scope">
            <span className={`tag scope ${scope}`}>{SCOPE_WORDS[scope]}</span>
            <span className="muted small">{SCOPE_BLURB[scope]}</span>
          </p>

          <label className="add-field wide">
            <span>Because</span>
            <textarea
              className="correct-reason" rows={2} value={because} onChange={(ev) => setBecause(ev.target.value)}
              placeholder="Why is this true? The verification reads it, and so does the next person to open the rule."
            />
          </label>

          {preview && <p className="add-preview"><span className="kicker">It will say</span> {preview}</p>}

          <p className="correct-note muted small">
            {oneOfMissing
              ? 'Name at least one thing for it to match.'
              : missing.length > 0
                ? `Still needed: ${missing.join(', ').toLowerCase()}.`
                : because.trim().length < MIN_REASON
                  ? 'A because is required — the same floor every correction carries.'
                  : 'It goes on the ledger now and is verified tonight.'}
          </p>

          {save.isError && <p className="error small">{save.error instanceof RefusedError ? save.error.refusal.message : (save.error as Error).message}</p>}

          <div className="actions">
            <button className="button primary" disabled={!enough || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Recording…' : 'Put it on the ledger'}</button>
            <button className="button" onClick={() => { setKind(null); reset() }}>Cancel</button>
          </div>
        </>
      )}
    </div>
  )
}

/** Fold the flat form values into the nested definition each rule kind declares. Pure. */
function build(form: Form | null, values: Record<string, string>): Record<string, unknown> {
  if (!form) return {}
  const out: Record<string, unknown> = {}
  for (const field of form.fields) {
    const raw = values[field.path]
    if (raw === undefined || raw === '') continue
    const value: unknown = field.kind === 'number' ? Number(raw) : raw
    const [head, tail] = field.path.split('.')
    if (!tail) { out[head] = value; continue }
    const nested = (out[head] as Record<string, unknown>) ?? {}
    nested[tail] = value
    out[head] = nested
  }
  // a cue's verdict is { <field>: <value> }, not { field, value }
  const verdict = out.verdict as { field?: string; value?: unknown } | undefined
  if (verdict?.field) out.verdict = { [verdict.field]: verdict.value ?? '' }
  return out
}
